import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { PageShell } from "../components/PageShell";
import { CmsItemView } from "./EssayReaderV3";
import { CmsProjectView } from "./ProjectDetailPage";
import { CmsMemoireView } from "./MemoireDetailPage";
import { API } from "../lib/content";

/**
 * PreviewPage — /preview/:token. Token-protected draft preview.
 *
 * Renders the draft with the exact same public layout as the published
 * page (shared CmsItemView / CmsProjectView renderers), including images,
 * PDFs and metadata. The page carries noindex/nofollow, is linked from
 * nowhere public, appears in no sitemap, and unknown tokens get the same
 * quiet "not valid" state — nothing about drafts is revealed.
 */
export default function PreviewPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [status, setStatus] = useState("loading");
    const originalTitle = useRef(document.title);

    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        meta.setAttribute("data-preview-noindex", "true");
        document.head.appendChild(meta);
        return () => {
            if (meta.parentNode) meta.parentNode.removeChild(meta);
            document.title = originalTitle.current;
        };
    }, []);

    useEffect(() => {
        setStatus("loading");
        setData(null);
        axios
            .get(`${API}/preview/${token}`)
            .then((r) => { setData(r.data); setStatus("ready"); })
            .catch(() => setStatus("missing"));
    }, [token]);

    if (status === "loading") {
        return (
            <PageShell testId="preview-page">
                <div className="py-16 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                    Loading preview…
                </div>
            </PageShell>
        );
    }

    if (status === "missing" || !data) {
        return (
            <PageShell testId="preview-page">
                <div className="py-16">
                    <p data-testid="preview-invalid" className="font-display italic text-[24px] md:text-[32px] leading-[1.2] text-[color:var(--navy)] max-w-[36ch]">
                        This preview link is not valid.
                    </p>
                    <Link to="/" className="mt-10 inline-block link-editorial font-mono text-[11px] tracking-[0.28em] uppercase">
                        ← Life is a Project
                    </Link>
                </div>
            </PageShell>
        );
    }

    const { type, item } = data;

    return (
        <>
            <div data-testid="preview-banner" className="relative z-20 border-b border-[color:var(--gold)]/50 bg-[color:var(--ivory)] pt-16 lg:pt-0">
                <div className="lg:pl-[var(--sidebar-w)]">
                    <div className="max-w-[1120px] mx-auto px-6 md:px-10 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        <span className="flex items-center gap-3 text-[color:var(--navy)]">
                            <span aria-hidden className="inline-block w-4 h-px bg-[color:var(--gold)]" />
                            Draft preview · {(item.status || "draft").toUpperCase()}
                        </span>
                        <Link
                            to={`/admin/content/${type}/${item.id}/edit`}
                            data-testid="preview-edit-link"
                            className="link-editorial hover:text-[color:var(--navy)]"
                        >
                            Edit this draft
                        </Link>
                        <Link
                            to={`/admin?filter=${type}`}
                            data-testid="preview-back-link"
                            className="link-editorial hover:text-[color:var(--navy)]"
                        >
                            Content manager
                        </Link>
                    </div>
                </div>
            </div>
            {type === "projects"
                ? <CmsProjectView item={item} previewToken={token} />
                : type === "memoire"
                    ? <CmsMemoireView item={item} previewToken={token} />
                    : <CmsItemView item={item} mode={type} previewToken={token} />}
        </>
    );
}
